import { toast } from "sonner";
import PdfProgressToast from "@/components/PdfProgressToast";
import { api } from "@/lib/api";

/**
 * Helper único para descargar un PDF mostrando un pill flotante con anillo de
 * progreso y cuenta atrás adaptativa. Aprende del historial del propio
 * dispositivo (localStorage) para estimar el tiempo cada vez con más precisión.
 *
 * @param {Object} opts
 * @param {string} opts.url       — ruta relativa al backend (ej: `/diagnostic/xxx/pdf`)
 * @param {string} opts.filename  — nombre con el que se descarga el fichero
 * @param {"std"|"tr"} opts.kind  — categoría de PDF (para el historial)
 * @param {string} [opts.label]   — título del toast
 * @param {number} [opts.timeout] — timeout de axios en ms (default 120000)
 *
 * @returns {Promise<{ok:true}>} si el download se completa
 * @throws el error de axios si algo falla (el llamante decide el mensaje
 *         final). El toast se cierra automáticamente en caso de error.
 */
const HISTORY_KEYS = {
  std: "pdf_dur_history_std",
  tr: "pdf_dur_history_tr",
};
const DEFAULT_ETA_SEC = 15;
const MAX_HISTORY = 5;

function getEta(kind) {
  try {
    const raw = localStorage.getItem(HISTORY_KEYS[kind] || HISTORY_KEYS.std);
    if (!raw) return DEFAULT_ETA_SEC;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr) || arr.length === 0) return DEFAULT_ETA_SEC;
    const sorted = [...arr].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    // +1 s de colchón para reducir la sensación de "casi listo…" al final.
    return Math.max(4, Math.round(median + 1));
  } catch {
    return DEFAULT_ETA_SEC;
  }
}

function pushDuration(kind, seconds) {
  try {
    const key = HISTORY_KEYS[kind] || HISTORY_KEYS.std;
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    arr.push(Number(seconds.toFixed(2)));
    while (arr.length > MAX_HISTORY) arr.shift();
    localStorage.setItem(key, JSON.stringify(arr));
  } catch {
    /* localStorage full o bloqueado: no bloqueamos la descarga por esto */
  }
}

export async function downloadPdfWithProgress({
  url,
  filename,
  kind = "std",
  label = "Preparando tu informe",
  timeout = 120000,
  variant = "default",
}) {
  const eta = getEta(kind);
  const start = Date.now();
  const toastId = `pdf-progress-${Date.now()}`;

  toast.custom(() => <PdfProgressToast eta={eta} label={label} state="progress" variant={variant} />, {
    id: toastId,
    duration: Infinity,
  });

  try {
    const res = await api.get(url, { responseType: "blob", timeout });
    const durSec = (Date.now() - start) / 1000;
    pushDuration(kind, durSec);

    const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);

    toast.custom(() => <PdfProgressToast eta={eta} label={label} state="done" variant={variant} />, {
      id: toastId,
      duration: 1800,
    });
    return { ok: true };
  } catch (err) {
    toast.dismiss(toastId);
    throw err;
  }
}
